import React from 'react';
import { useAppSelector } from 'hooks';
import { Form, Formik } from 'formik';
import { Alert } from 'reactstrap';

import { getErrorMessage } from '~libs/axiosWrapper';
import { formatDateString } from '~libs/dayjs';
import { useToast } from '~libs/toast';

import { Button, ErrorResponse, Modal, Spinner } from '~components';
import { FieldProps, Fields } from '~components/form-fields';

import { CarePlanType } from '~clients/models';

import { carePlanFormSchema, carePlanTypeOptions } from '~clients/forms/care-plans/carePlanForm';

import { useFieldValidationHighlights } from '~clients/hooks/useFieldValidationHighlights';

import { useCreateCarePlanMutation } from '~clients/actions/carePlans';

import {
  selectClientPackageSchedules,
  selectClientPackageSchedulesLoading,
} from '~main/selectors/clients/clientPackageSchedules';

interface ComponentProps {
  clientId: number;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  /** Called with the new plan's id on success — the view selects it + opens Overview. */
  onCreated: (planId: string) => void;
}

interface CarePlanFormValues {
  [key: string]: string | number | boolean | object | null;
  plan_type: CarePlanType | '';
  package_id: number | '';
  start_date: string;
  end_date: string;
  is_rights_explained: boolean;
  is_service_agreement_signed: boolean;
}

/**
 * Create-care-plan modal. Client is pre-selected (clientId goes straight into the
 * mutation, not a field). Package is required, sourced from the client's schedules
 * (stored as the package definition id the backend requires on create). Follows the
 * sibling *FormModal pattern.
 */
const CarePlanFormModal: React.FC<ComponentProps> = ({ clientId, isOpen, setIsOpen, onCreated }) => {
  const { showToast } = useToast();
  const [createCarePlan, { isLoading, error, reset }] = useCreateCarePlanMutation();
  const { invalidFields, validate } = useFieldValidationHighlights(carePlanFormSchema);

  const schedules = useAppSelector(selectClientPackageSchedules);
  const schedulesLoading = useAppSelector(selectClientPackageSchedulesLoading);

  // A failed attempt's error would otherwise resurface when the modal reopens.
  React.useEffect(() => {
    if (!isOpen) reset();
  }, [isOpen, reset]);

  // Same package sourcing as the Overview PackageScheduleSection.
  const clientSchedules = schedules.filter((schedule) => schedule.client_id === clientId);
  const packageOptions = clientSchedules.map((schedule) => ({
    id: schedule.package.id,
    name: `${schedule.package.name} (${formatDateString(schedule.start_date)} – ${formatDateString(schedule.end_date)})`,
  }));
  const hasNoPackages = !schedulesLoading && packageOptions.length === 0;

  const fields: FieldProps = {
    plan_type: {
      type: 'select',
      caption: 'Plan type',
      required: true,
      options: carePlanTypeOptions,
      placeholder: '-- Select a plan type --',
      dataCy: 'care-plan-plan-type',
    },
    package_id: {
      type: 'select',
      caption: 'Package',
      required: true,
      options: packageOptions,
      placeholder: '-- Select a package --',
      dataCy: 'care-plan-package',
    },
    start_date: { type: 'date', caption: 'Start date', colSize: 6, dataCy: 'care-plan-start-date' },
    end_date: { type: 'date', caption: 'End date', colSize: 6, dataCy: 'care-plan-end-date' },
    is_rights_explained: {
      type: 'switch',
      caption: 'Statement of Rights confirmed',
      dataCy: 'care-plan-rights-explained',
    },
    is_service_agreement_signed: {
      type: 'switch',
      caption: 'Service agreement signed',
      dataCy: 'care-plan-service-agreement',
    },
  };
  const definitions = Object.entries(fields).map(([key, props]) => ({ key, ...props }));

  // Default to the sole schedule when the client has exactly one; otherwise force a choice.
  const initialValues: CarePlanFormValues = {
    plan_type: '',
    package_id: packageOptions.length === 1 ? packageOptions[0].id : '',
    start_date: '',
    end_date: '',
    is_rights_explained: false,
    is_service_agreement_signed: false,
  };

  const onSubmit = async (values: CarePlanFormValues) => {
    try {
      const created = await createCarePlan({
        clientId,
        plan_type: values.plan_type as CarePlanType,
        package_id: Number(values.package_id),
        start_date: values.start_date || null,
        end_date: values.end_date || null,
        is_rights_explained: values.is_rights_explained,
        is_service_agreement_signed: values.is_service_agreement_signed,
      }).unwrap();
      // The created plan is inserted into the cached list by createCarePlan's onQueryStarted,
      // so the view's auto-select below resolves to it immediately (not sortedPlans[0]).
      showToast('success', 'Care plan created');
      setIsOpen(false);
      onCreated(created.id);
    } catch {
      // Surfaced through the mutation error below.
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      header="Create Care Plan"
      includeFooter={false}
      bodyClassName="care-plan-form-body"
      size="xl"
    >
      {error !== undefined && <ErrorResponse dataCy="care-plan-form-error" message={getErrorMessage(error)} />}
      {hasNoPackages && (
        <Alert color="warning" className="care-plan-alert" data-cy="care-plan-no-packages">
          This client has no package schedule. A care plan can&apos;t be created until one exists.
        </Alert>
      )}
      {/* Mount Formik only once schedules resolve — the single-schedule package default
          shifts initialValues, and enableReinitialize would otherwise wipe in-progress input. */}
      {schedulesLoading ? (
        <div className="care-plan-section-loading" data-cy="care-plan-form-loading">
          <Spinner loading />
        </div>
      ) : (
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={carePlanFormSchema}
          validate={validate}
          validateOnMount={false}
          validateOnChange={invalidFields.length > 0}
          validateOnBlur={false}
          onSubmit={onSubmit}
        >
        {({ values, setFieldValue, setFieldTouched, touched, dirty }) => (
          <Form>
            <Fields
              definitions={definitions}
              values={values}
              setFieldValue={setFieldValue}
              setFieldTouched={setFieldTouched}
              touched={touched}
              invalidFields={invalidFields}
            />
            <div className="care-plan-form-actions">
              <Button size="sm" color="warning" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                color="success"
                disabled={!dirty || hasNoPackages}
                loading={isLoading}
                dataCy="care-plan-save"
              >
                Create Care Plan
              </Button>
            </div>
          </Form>
        )}
        </Formik>
      )}
    </Modal>
  );
};

export default CarePlanFormModal;
